'use client'

import useFooter from './useFooter'
import styled from 'styled-components'
import { useRef } from 'react'
import { Media, MediaContextProvider } from '../Media'
import CookieSettings from '../CookieSettings'
import { usePathname } from 'next/navigation'

const Footer = () => {
  const footerRef = useRef(null)
  const { footerData } = useFooter(footerRef)
  const pathname = usePathname()

  // Layout for Small Screens (xs, sm)
  const smallLayout = (
    <div className='flex flex-row flex-wrap pb-[50px]'>
      {/* Left Column with two inner columns */}
      <div className='w-full flex flex-row flex-wrap px-4 pt-4 pb-[50px]'>
        {/* Inner Left */}
        <div className='w-1/2 flex flex-col font-swinton font-normal text-[16px] leading-[100%] tracking-[-0.05em]'>
          <div className='mb-3'>{footerData.QuicklinksHeading}</div>
          <a href={footerData.ContactLink} target='_blank'>
            {footerData.ContactText}
          </a>
          <a href={footerData.ConditionsLink} target='_blank'>
            {footerData.ConditionsText}
          </a>
          <a href={footerData.PressLink} target='_blank'>
            {footerData.PressText}
          </a>
          <CookieSettings label={footerData.CookiesText} />
          <div className='mb-3 mt-3'>{footerData.SocialHeading}</div>
          <a href={footerData.InstagramLink} target='_blank'>
            {footerData.InstagramText}
          </a>
        </div>
        {/* Inner Right */}
        <div className='w-1/2 flex flex-col font-swinton font-normal text-[16px] leading-[100%] tracking-[-0.05em] ps-5'>
          <div className='mb-3'>{footerData.ArtBaselHeading}</div>
          <a href={footerData.ArtBaselHongKongLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselHongKongText}
          </a>
          <a href={footerData.ArtBaselBaselLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselBaselText}
          </a>
          <a href={footerData.ArtBaselParisLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselParisText}
          </a>
          <a href={footerData.ArtBaselMiamiBeachLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselMiamiBeachText}
          </a>
        </div>
      </div>

      {/* Right Column */}
      <div className='w-full px-4 flex flex-row justify-between items-start gap-[65px]'>
        <a href={footerData.UBSLogoLink} target='_blank'>
          <img src={`/svgs/ubs-logo-v2.svg`} alt='Global Lead Partner UBS' className='w-[70px] h-[31px]' />
        </a>
        <img src='/svgs/boss-logo-v2.svg' alt='Partner Boss' className='w-[68px] h-[34px] float-right' />
      </div>
    </div>
  )

  // Layout for Medium Screens (md) - Structurally similar to small, styled by Tailwind's md: prefixes
  const mediumLayout = (
    <div className='flex flex-row flex-wrap pb-[50px]'>
      {/* Left Column with two inner columns */}
      <div className='w-full md:w-1/2 flex flex-row flex-wrap p-4'>
        {/* Inner Left */}
        <div className='w-1/2 flex flex-col font-swinton font-normal text-[16px] leading-[100%] tracking-[-0.05em]'>
          <div className='mb-3'>{footerData.QuicklinksHeading}</div>
          <a href={footerData.ContactLink} target='_blank'>
            {footerData.ContactText}
          </a>
          <a href={footerData.ConditionsLink} target='_blank'>
            {footerData.ConditionsText}
          </a>
          <a href={footerData.PressLink} target='_blank'>
            {footerData.PressText}
          </a>
          <CookieSettings label={footerData.CookiesText} />
          <div className='mb-3 mt-3'>{footerData.SocialHeading}</div>
          <a href={footerData.InstagramLink} target='_blank'>
            {footerData.InstagramText}
          </a>
        </div>
        {/* Inner Right */}
        <div className='w-1/2 flex flex-col font-swinton font-normal text-[16px] leading-[100%] tracking-[-0.05em] ps-5 md:ps-0'>
          <div className='mb-3'>{footerData.ArtBaselHeading}</div>
          <a href={footerData.ArtBaselHongKongLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselHongKongText}
          </a>
          <a href={footerData.ArtBaselBaselLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselBaselText}
          </a>
          <a href={footerData.ArtBaselParisLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselParisText}
          </a>
          <a href={footerData.ArtBaselMiamiBeachLink} target='_blank' className='text-nowrap'>
            {footerData.ArtBaselMiamiBeachText}
          </a>
        </div>
      </div>

      {/* Right Column */}
      <div className='w-full md:w-1/2 p-4 flex flex-row justify-around'>
        <a href={footerData.UBSLogoLink} target='_blank'>
          <img
            src={`/svgs/ubs-logo-v2.svg`}
            alt='Global Lead Partner UBS'
            className='w-[144px] h-[63px] md:w-[112px] md:h-[49px]'
          />
        </a>
        <img
          src='/svgs/boss-logo-v2.svg'
          alt='Partner Boss'
          className='w-[142px] h-[71px] md:w-[117px] md:h-[56px] float-right'
        />
      </div>
    </div>
  )

  // Layout for Large Screens (lg, xl, xxl)
  const largeLayout = (
    <div className='grid grid-cols-8 gap-4 px-8 pb-[50px] pt-[24px] items-start font-swinton'>
      <div className="w-[39px] h-[59px] bg-[url('/svgs/ab-awards-logo-only.svg')] bg-no-repeat bg-center"></div>
      <div className=''>
        <a href={footerData.UBSLogoLink} target='_blank'>
          <img
            src={`/svgs/ubs-logo-v2.svg`}
            alt='Global Lead Partner UBS'
            className='ubs-logo-footer mt-1 w-[144px] h-[63px]'
          />
        </a>
      </div>
      {/* Spacing */}
      <div />
      <div
        className={`flex flex-col font-swinton font-normal text-[19px] leading-[100%] tracking-[-0.05em] align-middle`}
      >
        <div className='mb-3'>{footerData.QuicklinksHeading}</div>
        <a href={footerData.ContactLink} target='_blank'>
          {footerData.ContactText}
        </a>
        <a href={footerData.ConditionsLink} target='_blank'>
          {footerData.ConditionsText}
        </a>
        <a href={footerData.PressLink} target='_blank'>
          {footerData.PressText}
        </a>
        <CookieSettings label={footerData.CookiesText} />
      </div>
      <div
        className={`flex flex-col font-swinton font-normal text-[19px] leading-[100%] tracking-[-0.05em] align-middle`}
      >
        <div className='mb-3'>{footerData.SocialHeading}</div>
        <a href={footerData.InstagramLink} target='_blank'>
          {footerData.InstagramText}
        </a>
      </div>
      <div className={`flex flex-col whitespace-nowrap text-[19px] leading-[100%] tracking-[-0.05em] align-middle`}>
        <div className='mb-3'>{footerData.ArtBaselHeading}</div>
        <a href={footerData.ArtBaselHongKongLink} target='_blank'>
          {footerData.ArtBaselHongKongText}
        </a>
        <a href={footerData.ArtBaselBaselLink} target='_blank'>
          {footerData.ArtBaselBaselText}
        </a>
        <a href={footerData.ArtBaselParisLink} target='_blank'>
          {footerData.ArtBaselParisText}
        </a>
        <a href={footerData.ArtBaselMiamiBeachLink} target='_blank'>
          {footerData.ArtBaselMiamiBeachText}
        </a>
      </div>
      <div />
      <img src='/svgs/boss-logo-v2.svg' alt='Partner Boss' className='w-[142px] h-[71px] float-right' />
    </div>
  )

  return (
    <MediaContextProvider>
      <StyledAbsolute className={`${pathname === '/' ? 'snap-start' : ''} snap-footer`} ref={footerRef}>
        <StylesFooter>
          <div className='p-4 md:p-8 iframe-wrapper mb-9'>
            {/* Specific Logo only for < md screens */}
            <Media lessThan='md'>
              <div className="w-[39px] h-[78px] bg-[url('/svgs/ab-awards-logo-only.svg')] bg-no-repeat bg-center my-5"></div>
            </Media>
            <IframeStyled
              id='xm-form'
              src='https://forms.mch-group.com/formcycle/form/alias/mch-group/form_2105_2/?xfc-height-changed-evt=true'
              style={{ width: '100%' }}
              scrolling='no'
            />
          </div>
          {/* Small Screens Layout */}
          <Media lessThan='md'>{smallLayout}</Media>
          {/* Medium Screens Layout */}
          <Media between={['md', 'lg']}>{mediumLayout}</Media>
          {/* Large Screens Layout */}
          <Media greaterThanOrEqual='lg'>{largeLayout}</Media>
        </StylesFooter>
      </StyledAbsolute>
      <div className='snap-section-start hide-floating-logos' />
    </MediaContextProvider>
  )
}

const IframeStyled = styled.iframe`
  height: 550px;

  @media (width >=768px) {
    height: 400px;
  }
`

const StyledAbsolute = styled.div`
  width: 100%;
`

const StylesFooter = styled.footer`
  font-family: var(--swintonfont);

  nav {
    right: 30px;
    top: 30px;
    position: relative;
    font-size: 19px;
  }

  a {
    color: #1e1e1e;
    display: block;
  }

  div.iframe-wrapper {
    border-bottom: 1px solid #1e1e1e;
  }
`

export default Footer
